// @flow
import type { ComponentType } from 'react';
import React from 'react';
import { Trans } from '@lingui/react';
import styled, { type StyledComponent, css } from 'styled-components';

import { A11yHidden, Color, DSTokenMap, FocusOutline, MQ } from '@rover/kibble/styles';
import { type Props as CommonProps } from '@rover/react-lib/components/buttons/SelectButton/SelectButton.types';
import CalloutBadge from '@rover/react-lib/components/callouts/CalloutBadge';
import Icon from '@rover/react-lib/components/typography/Icon/Icon';
import { useI18n } from '@rover/react-lib/utils/i18n';
import type { Untranslated } from '@rover/shared/js/types';

export type Props = {
  ...CommonProps,
  allowSelectMultiple: boolean,
  icon: string | ComponentType<{}>,
  title: Untranslated,
  label?: string,
  inputGroupName?: string,
  displayAs?: ?'compact' | 'expanded',
};

type StyledLabelProps = {
  selected: boolean,
  hasIcon: boolean,
  disabled?: boolean,
  displayAs?: ?'compact' | 'expanded',
};

const compactLabelStyles = (hasIcon) => `
  justify-content: ${hasIcon ? 'flex-start' : 'center'};
`;

const expandedLabelStyles = `justify-content: center;`;

export const SelectButtonLabel: StyledComponent<StyledLabelProps> = styled.div`
  padding: 15px 25px;
  margin: 0;
  display: inline-block;
  font-size: 14px;
  border-width: 1px;
  border-radius: 4px;
  color: ${DSTokenMap.TEXT_COLOR_TERTIARY};
  border-color: ${DSTokenMap.BORDER_COLOR_PRIMARY};
  white-space: normal;
  position: relative;
  display: flex;
  flex: 1;
  font-weight: normal;
  user-select: none;
  box-shadow: inset 0 0 0 1px ${DSTokenMap.BORDER_COLOR_PRIMARY};
  cursor: pointer;

  &:not(:first-of-type) {
    margin-left: 12px;
  }

  &:hover {
    color: ${DSTokenMap.TEXT_COLOR_SECONDARY};
    ${({ disabled }) =>
      !disabled
        ? ''
        : css`
            cursor: not-allowed;
            color: ${DSTokenMap.TEXT_COLOR_TERTIARY};
          `};
  }

  &:active {
    border-color: ${DSTokenMap.INTERACTIVE_BORDER_COLOR_PRIMARY_ACTIVE};
    background-color: ${DSTokenMap.BACKGROUND_COLOR_PRIMARY};
    box-shadow: inset 0 0 0 2px ${DSTokenMap.INTERACTIVE_BORDER_COLOR_PRIMARY_ACTIVE};
  }

  ${FocusOutline({ zIndex: 0, outlineOffset: 2 })}

  ${({ selected }) =>
    !selected
      ? ''
      : css`
          box-shadow: inset 0 0 0 2px ${DSTokenMap.INTERACTIVE_BORDER_COLOR_PRIMARY_ACTIVE};
          color: ${DSTokenMap.TEXT_COLOR_SECONDARY};
          border-color: ${DSTokenMap.INTERACTIVE_BORDER_COLOR_PRIMARY_ACTIVE};
          background-color: ${DSTokenMap.BACKGROUND_COLOR_PRIMARY};
        `}

  ${({ displayAs, hasIcon }) => {
    if (displayAs === 'compact') {
      return compactLabelStyles(hasIcon);
    }
    if (displayAs === 'expanded') {
      return expandedLabelStyles;
    }

    return `
    ${expandedLabelStyles};
    ${MQ.MD_UP} {${compactLabelStyles(hasIcon)}}`;
  }}

  ${({ disabled }) =>
    !disabled
      ? ''
      : css`
          background-color: ${DSTokenMap.BACKGROUND_COLOR_SECONDARY};
          border-color: ${DSTokenMap.INTERACTIVE_BORDER_COLOR_DISABLED};
          box-shadow: inset 0 0 0 1px ${DSTokenMap.INTERACTIVE_BORDER_COLOR_DISABLED};
          color: ${DSTokenMap.TEXT_COLOR_DISABLED};
        `};
`;

type WrapperProps = {
  displayAs?: ?'compact' | 'expanded',
};

const compactWrapperStyles = `flex-direction: row;`;

const expandedWrapperStyles = `flex-direction: column;`;

export const Wrapper: StyledComponent<WrapperProps> = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  text-align: center;
  justify-content: center;

  ${({ displayAs }) => {
    if (displayAs === 'compact') {
      return compactWrapperStyles;
    }
    if (displayAs === 'expanded') {
      return expandedWrapperStyles;
    }

    return `
    ${expandedWrapperStyles};
    ${MQ.MD_UP} {${compactWrapperStyles}}`;
  }}
`;

type IconWrapperProps = {
  displayAs?: ?'compact' | 'expanded',
};

const compactIconWrapperStyles = `
  margin-bottom: 0;
  margin-right: 10px;`;

const expandedIconWrapperStyles = `margin-bottom: 6px;`;

export const IconWrapper: StyledComponent<IconWrapperProps> = styled.div`
  min-width: 24px;
  max-width: 24px;
  min-height: 24px;
  max-height: 24px;

  ${({ displayAs }) => {
    if (displayAs === 'compact') {
      return compactIconWrapperStyles;
    }
    if (displayAs === 'expanded') {
      return expandedIconWrapperStyles;
    }

    return `
    ${expandedIconWrapperStyles};
    ${MQ.MD_UP} {${compactIconWrapperStyles}}`;
  }}

  & > svg {
    min-width: 24px;
    max-width: 24px;
    min-height: 24px;
    max-height: 24px;
    fill: ${Color.GREEN_BASE};
  }

  & > i {
    font-size: 24px;
    color: ${DSTokenMap.INTERACTIVE_TEXT_COLOR_PRIMARY_ACTIVE};
  }
`;

const StyledHiddenInput: StyledComponent<{}> = styled.input`
  ${A11yHidden}
`;

type ButtonLabelProps = {
  icon?: boolean,
  displayAs?: ?'compact' | 'expanded',
};

const compactButtonLabelStyles = (icon) => `
  width: 100%;
  text-align: ${icon ? 'left' : 'center'};
`;

const expandedButtonLabelStyles = ` width: 100%; text-align: center;`;

export const ButtonLabel: StyledComponent<ButtonLabelProps> = styled.div`
  ${({ displayAs, icon }) => {
    if (displayAs === 'compact') {
      return compactButtonLabelStyles(icon);
    }
    if (displayAs === 'expanded') {
      return expandedButtonLabelStyles;
    }

    return `
    ${expandedButtonLabelStyles};
    ${MQ.MD_UP} {${compactButtonLabelStyles(icon)}}`;
  }}
`;

export const Title: StyledComponent<{}> = styled.div`
  font-weight: 900;
`;

type StyledCalloutBadgeProps = React$ElementConfig<typeof CalloutBadge>;

const StyledCalloutBadge: StyledComponent<StyledCalloutBadgeProps> = styled(CalloutBadge)`
  position: absolute;
  top: -4px;
  left: -4px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const renderIcon = (ButtonIcon: string | ComponentType<{}>) =>
  typeof ButtonIcon === 'string' ? <Icon icon={ButtonIcon} /> : <ButtonIcon />;

const renderSubtitle = (subtitle) => {
  if (!subtitle) return null;

  return (
    <div>
      <Trans id={subtitle} />
    </div>
  );
};

const renderTitle = (title, subtitle, displayAs, icon) => {
  if (!title && !subtitle) return null;

  return (
    <ButtonLabel icon={!!icon} displayAs={displayAs}>
      <Title>
        <Trans id={title} />
      </Title>
      {renderSubtitle(subtitle)}
    </ButtonLabel>
  );
};

const SelectButton = ({
  allowSelectMultiple,
  selected,
  disabled,
  icon,
  title,
  subtitle,
  label,
  inputGroupName,
  displayAs,
  onClick,
  ...other
}: Props) => {
  const { i18n } = useI18n();
  return (
    <SelectButtonLabel
      disabled={disabled}
      displayAs={displayAs}
      hasIcon={!!icon}
      onClick={!disabled ? onClick : () => {}}
      selected={selected}
      {...other}
    >
      {label && <StyledCalloutBadge value={label} />}
      <Wrapper displayAs={displayAs}>
        {icon && <IconWrapper displayAs={displayAs}>{renderIcon(icon)}</IconWrapper>}
        {renderTitle(title, subtitle, displayAs, icon)}
        <StyledHiddenInput
          type={allowSelectMultiple ? 'checkbox' : 'radio'}
          name={inputGroupName}
          checked={selected}
          aria-checked={selected}
          aria-label={i18n._(title)}
          onChange={!disabled ? onClick : () => {}}
        />
      </Wrapper>
    </SelectButtonLabel>
  );
};

SelectButton.defaultProps = {
  value: '',
  allowSelectMultiple: true,
  selected: false,
  disabled: false,
  title: '',
  subtitle: '',
  label: '',
  onClick: () => {},
};

export default SelectButton;



// WEBPACK FOOTER //
// ../react-lib/src/components/buttons/SelectButton/SelectButton.jsx